import React from 'react';
import { Box, Typography } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { DESIGN_TOKENS } from '../../../styles/constants';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  totalItems: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  onPageChange,
}) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const renderPageNumbers = () => {
    const pages: (number | string)[] = [];
    
    // Always show first page
    pages.push(1);
    
    if (currentPage > 3) {
      pages.push('...');
    }
    
    // Show current page and adjacent pages
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      if (i !== 1 && i !== totalPages) {
        pages.push(i);
      }
    }
    
    if (currentPage < totalPages - 2) {
      pages.push('...');
    }
    
    // Always show last page if more than 1 page
    if (totalPages > 1) {
      pages.push(totalPages);
    }
    
    return pages;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '24px',
      }}
    >
      <Box sx={{ display: 'flex', borderRadius: '16px' }}>
        {renderPageNumbers().map((page, index) => (
          <Box
            key={index}
            onClick={() => typeof page === 'number' && onPageChange(page)}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px',
              padding: '0px 32px',
              width: '56px',
              height: '56px',
              backgroundColor: currentPage === page ? '#F4DBC5' : 'transparent',
              borderRadius: '60px',
              cursor: typeof page === 'number' ? 'pointer' : 'default',
              '&:hover': {
                backgroundColor: typeof page === 'number' && currentPage !== page ? '#F6F8F8' : undefined,
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: DESIGN_TOKENS.typography.fontFamily,
                fontSize: '16px',
                fontWeight: currentPage === page ? 700 : 400,
                lineHeight: '22.4px',
                color: '#181818',
              }}
            >
              {page}
            </Typography>
          </Box>
        ))}
        
        {currentPage < totalPages && (
          <Box
            onClick={() => onPageChange(currentPage + 1)}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px',
              padding: '0px 32px',
              height: '56px',
              borderRadius: '60px',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: '#F6F8F8',
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: DESIGN_TOKENS.typography.fontFamily,
                fontSize: '16px',
                fontWeight: 400,
                lineHeight: '22.4px',
                color: '#181818',
              }}
            >
              Next
            </Typography>
            <ChevronRight sx={{ fontSize: 24, color: '#181818' }} />
          </Box>
        )}
      </Box>
      
      <Typography
        sx={{
          fontFamily: DESIGN_TOKENS.typography.fontFamily,
          fontSize: '16px',
          fontWeight: 400,
          lineHeight: '22.4px',
          color: '#93989C',
        }}
      >
        {startItem}–{endItem} of {totalItems} reviews
      </Typography>
    </Box>
  );
};

export default Pagination;


